/**
 * Use the API in the TagUtils module to handle the tags of a case.
 * @module TagUtils
 */
import type { AxiosError } from 'axios';

import { C11nEnv } from '../interpreter/c11n-env';
import { invokeRestApi } from '../rest-client';
import DataPageUtils from '../datapage';

const ADD_TAGS = 'addTagsToCase';
const DELETE_TAG = 'deleteCaseTag';
const GET_TAGS = 'getCaseTags';

type TagItem = {
  Name: string;
};

/**
 * Obtains the view to render the list of cases associated with a specific tag.
 * @function
 * @static
 * @example <caption>In this example, the API obtains the view that renders the list of cases associated with a specific tag in a case whose ID is W-102.</caption>
 * PCore.getTagUtils().getTaggedCases('ORG-APP-WORK W-102', getPConnect())
 * .then(tags => {
 *    // tags array
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param caseID        The ID of the case pointing to pzInsKey value from where the view must be downloaded. Note: Ensure that you provide the pzInsKey value of the caseID.
 * @param pConnectObj   The PConnect object of the component from where the API is being called.
 * @returns A Promise that resolves to an object.
 */
export const getTaggedCases = (caseID: string, pConnectObj: C11nEnv): Promise<object> => {
  const queryPayload = { caseID, viewID: 'pyTaggedCases' };
  return invokeRestApi('loadView', { queryPayload }, pConnectObj.getContextName())
    .then((response) => response?.data)
    .catch((error) => {
      pConnectObj.reportError(': Error occurred during ajax call at fetchTaggedcases API : ', error.response);
    });
};

/**
 * Obtains the tags of a specific case.
 * @function
 * @static
 * @example <caption>In this example, the API obtains the tags of a case whose ID is W-102.</caption>
 * PCore.getTagUtils().getTags('ORG-APP-WORK W-102', getPConnect())
 * .then(tags => {
 *    // tags array
 * }).catch(err => {
 *   // errors
 * });
 * @param caseID        The ID of the case pointing to pzInsKey value whose tags must be obtained. Note: Ensure that you provide the pzInsKey value of the caseID.
 * @param pConnectObj   The PConnect object of the component from where the API is being called.
 * @returns A Promise that resolves to an object.
 */
export const getTags = (caseID: string, pConnectObj: C11nEnv): Promise<object> => {
  const queryPayload = {
    caseID
  };
  return invokeRestApi(GET_TAGS, { queryPayload }, pConnectObj.getContextName())
    .then((response) => response?.data?.tagsList)
    .catch((error) => {
      pConnectObj.reportError(': Error occurred during ajax call at fetchTags API : ', error.response);
    });
};

/**
 * Adds tags to a specific case.
 * @function
 * @static
 * @example <caption>In this example, the API adds the tags, Tag1 and Tag2 to the case whose ID is W-102.</caption>
 * PCore.getTagUtils().postTags('ORG-APP-WORK W-102', [{ Name : 'Tag1'}, {Name : 'Tag2'}], getPConnect())
 * .then(() => {
 *   // success
 * }).catch(err => {
 *    // Error handling
 * });
 *
 * @param caseID        The ID of the case to which the tags must be added. Note: Ensure that you provide the pzInsKey value of the caseID.
 * @param tags          The list of tags that must be added to the case.
 * @param pConnectObj   The PConnect object of the component from where the API is being called.
 * @returns A Promise that resolves to an object.
 */
export const postTags = (caseID: string, tags: TagItem[], pConnectObj: C11nEnv): Promise<object> => {
  const queryPayload = {
    caseID
  };
  const reqBody = JSON.stringify({
    tags
  });
  return invokeRestApi(ADD_TAGS, { body: reqBody, queryPayload }, pConnectObj.getContextName())
    .then((response) => response?.data?.results)
    .catch((error) => {
      pConnectObj.reportError(': Error occurred during ajax call at postTags API : ', error.response);
    });
};

/**
 * Removes a tag from a specific case.
 * @function
 * @static
 * @example <caption>In this example, the API removes the tag whose ID is Tag1 from the case whose ID is W-102.</caption>
 * PCore.getTagUtils().removeTag('ORG-APP-WORK W-102', 'Tag1', getPConnect())
 * .then(() => {
 *    // success
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param caseID    The ID of the case from which the tag must be removed. Note: Ensure that you provide the pzInsKey value of the caseID.
 * @param tagID     The ID of the tag that is to be removed from the case.
 * @param pConnectObj   The PConnect object of the component from where the API is being called.
 * @returns A Promise that resolves to an object.
 */
export const removeTag = (caseID: string, tagID: string, pConnectObj: C11nEnv) => {
  const queryPayload = {
    caseID,
    tagID
  };
  return invokeRestApi(DELETE_TAG, { queryPayload }, pConnectObj.getContextName()).catch((error) => {
    pConnectObj.reportError(': Error occurred during ajax call at removeTag API : ', error.response);
  });
};

/**
 * Gets URL for opening tags landing page in infinity application
 * @function
 * @static
 * @private
 * @example <caption>In this example, the API get the URL for tags Landing Page.</caption>
 * PCore.getTagUtils().getTagsLandingPageURL(getPConnect())
 * .then(() => {
 *    // success
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param pConnectObj   The PConnect object of the component from where the API is being called.
 * @returns A Promise that resolves to an object.
 */
const getTagsLandingPageURL = (pConnectObj: C11nEnv) => {
  const dataViewName = 'D_pxGetTagsLandingPageURL';
  return DataPageUtils.getPageDataAsync(
    dataViewName,
    pConnectObj.getContextName(),
    {},
    { invalidateCache: true }
  ).catch((error: AxiosError) => {
    pConnectObj.reportError(': Error occurred during ajax call getTagsLandingPageURL API : ', error.response);
  });
};

export default { getTaggedCases, getTags, postTags, removeTag, getTagsLandingPageURL };
